/**
 * Maps Core - Simple Google Maps for Embark Map List
 *
 * Provides a simple city overview map functionality for embark-map-list sections.
 * Automatically initializes when a map tab is clicked.
 */

class EmbarkMaps {
  constructor() {
    this.apiKey = "AIzaSyBIWR4UpcfIa-3cZ-pnNS7XcJOi5hHKBYk";
    this.mapId = "5d278bcd39e22421";
    this.apiLoaded = false;
    this.apiLoading = false;
    this.initializedMaps = new Set();

    this.init();
  }

  init() {
    document.addEventListener('DOMContentLoaded', () => {
      // Find all embark map sections and set up click handlers
      const mapTabs = document.querySelectorAll('[id^="tab-map"]');

      mapTabs.forEach(mapTab => {
        mapTab.addEventListener('click', () => {
          const mapId = 'locations-map';

          // Scroll to map on mobile when tab is clicked
          this.scrollToMapOnMobile();

          // Check if already initialized
          if (this.initializedMaps.has(mapId)) return;

          // Mark as loading to prevent double initialization
          this.initializedMaps.add(mapId);

          this.createCityMap(mapId);
        });
      });
    });
  }

  async createCityMap(mapId) {
    try {
      // Load Google Maps API if needed
      await this.loadGoogleMapsAPI();

      // Get location data from the embedded JSON (same locations as displayed in the list)
      const dataScript = document.querySelector('script.locations_data');

      if (!dataScript) {
        console.error('Embark Maps: No location data found for', mapId);
        this.showError(mapId, 'No location data found');
        return;
      }

      let locations;
      try {
        const jsonText = dataScript.textContent.trim();
        // The script contains an array of location objects, so wrap in array brackets
        const locationsArray = `${jsonText}`;
        locations = JSON.parse(locationsArray);
      } catch (error) {
        console.error('Embark Maps: Failed to parse location data:', error);
        this.showError(mapId, 'Invalid location data');
        return;
      }

      if (locations.length === 0) {
        console.error('Embark Maps: No locations found for', mapId);
        this.showError(mapId, 'No locations found');
        return;
      }



      // Create the map
      const container = document.getElementById(mapId);
      if (!container) {
        console.error('Embark Maps: Container not found for', mapId);
        return;
      }

      // Show loading state
      container.innerHTML = '<div style="display: flex; align-items: center; justify-content: center; height: 100%; background: #f5f5f5; color: #666;">Loading map...</div>';

      const map = new google.maps.Map(container, {
        mapId: this.mapId,
        zoom: 15,
        center: locations[0].location,
        clickableIcons: false,
        disableDefaultUI: true,
        gestureHandling: 'greedy',
        fullscreenControl: true,
        cameraControl: true,
        scaleControl: true
      });

      // Add markers and bounds
      const bounds = new google.maps.LatLngBounds();

      locations.forEach(location => {
        this.createMarker(map, location);
        bounds.extend(location.location);
      });

      // Fits map to bounds only if more than one location
      if (locations.length > 1) {
        map.fitBounds(bounds, 45);
      }

    } catch (error) {
      console.error('Embark Maps: Failed to create map for', mapId, error);
      this.showError(mapId, 'Failed to load map');
      // Remove from initialized set so user can try again
      this.initializedMaps.delete(mapId);
    }
  }

  createMarker(map, location) {
    // Create marker content
    const markerContent = document.createElement("div");
    markerContent.className = "tg-marker";
    markerContent.innerHTML = `
      <div class="marker-label">
        ${location.name}
        <p>View Details</p>
      </div>
      <div class="relative">
        <span class="marker-tap-area"></span>
        <svg class="custom-marker__icon" xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 0 24 24" width="24px" fill="#000">
            <path d="M12 2c-4.2 0-8 3.22-8 8.2 0 3.18 2.45 6.92 7.34 11.23.38.33.95.33 1.33 0C17.55 17.12 20 13.38 20 10.2 20 5.22 16.2 2 12 2zm0 10c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z"/>
        </svg>
      </div>
    `;

    // Create the marker with better default marker suppression
    const marker = new google.maps.marker.AdvancedMarkerElement({
      map,
      position: location.location,
      content: markerContent,
      title: location.name,
      gmpClickable: true,
      zIndex: 1000
    });

    // Force hide any default marker elements that might appear
    setTimeout(() => {
      const markerWrapper = markerContent.closest('gmp-advanced-marker');
      if (markerWrapper) {
        markerWrapper.style.zIndex = '1000';
        // Hide any default marker pin that might show
        const defaultPins = markerWrapper.querySelectorAll('[data-marker-type="pin"]');
        defaultPins.forEach(pin => pin.style.display = 'none');
      }
    }, 100);

    // Handle mobile vs desktop behavior - check for touch capability
    const isMobile = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

    if (isMobile) {
      // Mobile: First tap shows label, second tap (on label) goes to page
      marker.addEventListener('gmp-click', (e) => {
        // gmp-click may pass AdvancedMarkerClickEvent (use .domEvent) or a DOM-like event; see Maps samples.
        const raw = e.domEvent ?? e;
        const target = raw.target;
        if (!target) return;

        const markerLabel = markerContent.querySelector('.marker-label');
        if (markerLabel && (markerLabel.contains(target) || target === markerLabel)) {
          if (location.url) {
            raw.stopPropagation?.();
            window.location.href = location.url;
            return;
          }
        }

        raw.preventDefault?.();
        raw.stopPropagation?.();

        const markerWrapper = markerContent.closest('gmp-advanced-marker');
        const isCurrentlyActive = markerWrapper && markerWrapper.classList.contains('marker-active');

        if (isCurrentlyActive) {
          markerWrapper.classList.remove('marker-active');
        } else {
          this.activateMarker(markerContent, map, location);
          this.scrollToMapOnMobile();
        }
      });
    } else {
      markerContent.addEventListener('mouseenter', () => {
        this.activateMarker(markerContent);
      });

      markerContent.addEventListener('mouseleave', () => {
        const markerWrapper = markerContent.closest('gmp-advanced-marker');
        if (markerWrapper) {
          markerWrapper.classList.remove('marker-active');
        }
      });

      if (location.url) {
        marker.addEventListener('gmp-click', (e) => {
          const raw = e.domEvent ?? e;
          raw.preventDefault?.();
          raw.stopPropagation?.();
          window.location.href = location.url;
        });
      }
    }

    return marker;
  }

  async loadGoogleMapsAPI() {
    if (this.apiLoaded) return Promise.resolve();
    if (this.apiLoading) return this.apiLoadingPromise;

    this.apiLoading = true;

    this.apiLoadingPromise = new Promise((resolve, reject) => {
      if (window.google && window.google.maps) {
        this.apiLoaded = true;
        this.apiLoading = false;
        resolve();
        return;
      }

      window.initEmbarkMaps = () => {
        this.apiLoaded = true;
        this.apiLoading = false;
        resolve();
      };

      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${this.apiKey}&loading=async&callback=initEmbarkMaps&v=weekly&libraries=marker`;
      script.async = true;
      script.onerror = () => {
        this.apiLoading = false;
        reject(new Error('Failed to load Google Maps API'));
      }
      document.head.appendChild(script);
    });

    return this.apiLoadingPromise;
  }



  // Helper method to clear all active markers
  clearActiveMarkers() {
    const allMarkerWrappers = document.querySelectorAll('gmp-advanced-marker.marker-active');
    allMarkerWrappers.forEach(wrapper => {
      wrapper.classList.remove('marker-active');
    });
  }

  // Helper method to activate a marker
  activateMarker(markerContent, map, location) {
    this.clearActiveMarkers();
    const markerWrapper = markerContent.closest('gmp-advanced-marker');
    if (markerWrapper) {
      markerWrapper.classList.add('marker-active');
      if (map && location) {
        map.panTo(location.location);
      }
    }
  }

  // Helper method to scroll map into view on mobile
  scrollToMapOnMobile() {
    const isMobile = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    if (!isMobile) return;

    const mapContainer = document.querySelector('.map-container');
    if (mapContainer) {
      // Small delay to ensure DOM updates are complete
      setTimeout(() => {
        mapContainer.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }, 100);
    }
  }

  showError(mapId, message) {
    const container = document.getElementById(mapId);
    if (container) {
      container.innerHTML = `<div style="display: flex; align-items: center; justify-content: center; height: 100%; background: #f5f5f5; color: #666;">${message}</div>`;
    }
  }
}

// Initialize the embark maps system
new EmbarkMaps();

/**
 * Same rules as store-locator.js — live wait only when open and check-in not disabled.
 * Uses script.locations_data (barbershops-json shape).
 */
function storeEligibleForLiveWaitUi(location) {
  const storeOpen =
    location?.is_the_store_open === true || location?.is_the_store_open === "true";
  const checkInDisabled =
    location?.check_in_disable === true || location?.check_in_disable === "true";
  return storeOpen && !checkInDisabled;
}

function parseLocationsData() {
  const dataScript = document.querySelector("script.locations_data");
  if (!dataScript) return null;
  try {
    return JSON.parse(dataScript.textContent.trim());
  } catch {
    return null;
  }
}

/** Below Bootstrap `lg` the Details button is hidden (`d-none d-lg-flex` on embark-location-card) */
const MOBILE_CARD_NAV_MAX_WIDTH = "(max-width: 991.98px)";

/** Match store-locator.js — ignore only real controls, not `.location__actions` (includes non-clickable wait UI) */
function shouldIgnoreLocationCardBodyClick(target) {
  const el = target?.nodeType === 1 ? target : target?.parentElement;
  if (!el || !(el instanceof Element)) return true;
  return !!el.closest(
    "a, button, input, textarea, select, #fav-location-icon, [data-favorite-store], .fav-location"
  );
}

function initEmbarkMapListLocationCardClicks() {
  const locations = parseLocationsData();
  if (!locations?.length) return;

  const byShortcutId = new Map(
    locations.map((loc) => [String(loc.shortcuts_id), loc])
  );

  document.addEventListener("click", (event) => {
    const card = event.target.closest(".location");
    if (!card || !card.closest(".locations-wrapper")) return;
    if (shouldIgnoreLocationCardBodyClick(event.target)) return;

    const storeId = card.getAttribute("data-shortcut-id");
    if (!storeId) return;
    const location = byShortcutId.get(String(storeId));
    if (!location?.url) return;

    if (window.matchMedia(MOBILE_CARD_NAV_MAX_WIDTH).matches) {
      window.location.href = location.url;
    }
  });
}

function initEmbarkMapListWaitTimes() {
  const locations = parseLocationsData();
  if (!locations?.length) return;

  const byShortcutId = new Map(
    locations.map((loc) => [String(loc.shortcuts_id), loc])
  );

  document.querySelectorAll(".location").forEach((card) => {
    const waitTimeElement = card.querySelector(".js-wait-time");
    if (!waitTimeElement) return;

    const storeId = card.getAttribute("data-shortcut-id");
    if (!storeId) return;

    const location = byShortcutId.get(String(storeId));
    if (!location || !storeEligibleForLiveWaitUi(location)) return;

    document.dispatchEvent(
      new CustomEvent("dispatch:shop-data", {
        detail: {
          waitTimes: true,
          hours: false,
          wrapper: card,
          filter: storeId,
        },
      })
    );
  });
}

document.addEventListener("DOMContentLoaded", () => {
  initEmbarkMapListWaitTimes();
  initEmbarkMapListLocationCardClicks();
});

// The favorite-store sync in embark.js re-renders .locations-wrapper when the
// cart attribute changes; the fresh cards need their wait times re-initialized
document.addEventListener("locations-list:after:rendered", () => {
  initEmbarkMapListWaitTimes();
});
